import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { Award } from '@internal/plugin-neo4j-awards-common';
import type { AwardsApi } from './types';

export class AwardsBackendApi implements AwardsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getAwards(
    uid: string,
    name: string,
    owners: string[],
    recipients: string[],
  ): Promise<Award[]> {
    const url = `${await this.discoveryApi.getBaseUrl('awards')}/`;
    const query = new URLSearchParams();
    query.append('uid', uid);
    query.append('name', name);
    owners.forEach(owner => {
      query.append('owners', owner);
    });
    recipients.forEach(recipient => {
      query.append('recipients', recipient);
    });

    const resp = await this.fetchApi.fetch(`${url}?${query.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(
        `Failed to fetch awards: ${resp.status} ${resp.statusText} - ${text}`,
      );
    }

    const body = await resp.json();
    return Array.isArray(body) ? (body as Award[]) : [];
  }

  async save(award: Award): Promise<Award> {
    const url = `${await this.discoveryApi.getBaseUrl('awards')}/`;
    const method = award.uid === '' ? 'POST' : 'PUT';
    const suffix = award.uid === '' ? '' : `/${award.uid}`;

    const resp = await this.fetchApi.fetch(`${url}${suffix}`, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(award),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(
        `Failed to ${method === 'POST' ? 'create' : 'update'} award: ${
          resp.status
        } ${resp.statusText} - ${text}`,
      );
    }

    return (await resp.json()) as Award;
  }

  async delete(uid: string): Promise<boolean> {
    const url = `${await this.discoveryApi.getBaseUrl('awards')}/`;

    const resp = await this.fetchApi.fetch(`${url}/${uid}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(
        `Failed to delete award: ${resp.status} ${resp.statusText} - ${text}`,
      );
    }

    const body = await resp.json();
    return Boolean(body);
  }

  async uploadLogo(_file: File): Promise<{ location: string }> {
    throw new Error(
      'Uploading award logos is not supported in the Neo4j awards plugin; use a public image URL instead.',
    );
  }
}
